import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, CheckCircle2, Loader2 } from 'lucide-react';

/**
 * Staged "AI is thinking" progress for long-running LLM calls (template draft
 * + pool generation). The backend is opaque, so we pace the visible stages on
 * a timer tuned to the expected duration and HOLD on the last stage until the
 * real response arrives (the parent unmounts us when `running` flips false).
 *
 * The point is purely UX: make a 20-90s wait feel smart and transparent for
 * recruiters, without pretending to report real backend progress.
 */
const AiProgressSteps: React.FC<{
  steps: string[];
  running: boolean;
  title: string;
  /** ms between stage advances (the final stage then holds). */
  stepMs?: number;
  /**
   * Controlled mode: when provided, the active stage is driven by this index
   * (derived from REAL progress) instead of a timer -- so it never resets
   * misleadingly on refresh. Pass a fraction-based index from the caller.
   */
  currentStep?: number;
  /** Optional trailing node rendered under the progress bar (e.g. ETA). */
  footer?: React.ReactNode;
}> = ({ steps, running, title, stepMs = 2400, currentStep, footer }) => {
  const controlled = typeof currentStep === 'number';
  const [autoIdx, setAutoIdx] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (controlled || !running || steps.length === 0) {
      setAutoIdx(0);
      return;
    }
    setAutoIdx(0);
    let cur = 0;
    const tick = () => {
      cur = Math.min(cur + 1, steps.length - 1);
      setAutoIdx(cur);
      if (cur < steps.length - 1) timer.current = setTimeout(tick, stepMs);
    };
    timer.current = setTimeout(tick, stepMs);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [controlled, running, steps.length, stepMs]);

  if (!running || steps.length === 0) return null;

  const idx = controlled
    ? Math.max(0, Math.min(steps.length - 1, currentStep as number))
    : autoIdx;
  const pct = Math.round(((idx + 1) / steps.length) * 100);

  return (
    <div className="rounded-xl border border-pink-100 bg-gradient-to-br from-pink-50/70 to-white p-3.5">
      <div className="flex items-center gap-1.5 mb-2.5">
        <Sparkles className="w-3.5 h-3.5 text-pink-600" />
        <span className="text-xs font-semibold text-slate-700">{title}</span>
      </div>
      <ul className="space-y-1.5">
        {steps.map((s, i) => {
          const done = i < idx;
          const active = i === idx;
          return (
            <li key={i} className="flex items-center gap-2 text-xs">
              {done ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
              ) : active ? (
                <Loader2 className="w-3.5 h-3.5 text-pink-600 animate-spin flex-shrink-0" />
              ) : (
                <span className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                </span>
              )}
              <span
                className={
                  done
                    ? 'text-slate-500'
                    : active
                      ? 'text-slate-800 font-medium'
                      : 'text-slate-400'
                }
              >
                {s}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="mt-3 h-1 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-pink-400 to-pink-600 transition-all duration-700 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      {footer && <div className="mt-2">{footer}</div>}
    </div>
  );
};

export default AiProgressSteps;
